import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { checkQuantity } from "../utils/quantityCheck";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DeleteOutline as RemoveIcon } from "@mui/icons-material";

/**
 * Stock Transfer (web) - send stock from one branch to another without a POS counter,
 * including franchise branch to franchise branch inside a franchise's own login.
 *
 * "From" lists the user's own branches; "To" lists the branches the POS Stock Transfer
 * screen would offer them. The server checks both again and saves through the same
 * transfer-out the POS uses, so the receiving branch accepts it in Accept Stock as usual.
 */

const REASON_CODES = ["NORMAL DC", "EXPIRY AND DEFECT RETURN", "DC MISTAKE"];

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const nowTime = () => new Date().toTimeString().slice(0, 8);
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const money = (n) => (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) => (n == null ? "" : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 }));
const branchLabel = (b) => {
  const name = String(b.branchName || "").trim();
  const tag = b.isFranchiseBranch ? " [FR]" : "";
  return `${b.branchCode}${name ? ` - ${name}` : ""}${tag}`;
};

/** Item picker that searches the server as the user types, like Stock Conversion. */
function ItemPicker({ tenancyId, auth, disabled, onPick }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (disabled) return undefined;
    const q = query.trim();
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`/api/${tenancyId}/items/search`, { ...auth, params: { q, page: 0, size: 25 } });
        setOptions(res.data?.content || []);
      } catch {
        setOptions([]);
        setError(t("Could not search items."));
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, tenancyId, auth, disabled, t]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={null}
      disabled={disabled}
      loading={loading}
      blurOnSelect
      clearOnBlur
      onChange={(_, v) => {
        if (v) onPick(v);
        setQuery("");
      }}
      onInputChange={(_, v, why) => {
        if (why === "input") setQuery(v);
      }}
      filterOptions={(x) => x}
      noOptionsText={error || (query ? t("No items match") : t("Type to search items"))}
      getOptionLabel={(o) => (o ? `${o.itemName || ""}${o.itemCode ? ` (${o.itemCode})` : ""}` : "")}
      isOptionEqualToValue={(o, v) => o.itemId === v?.itemId}
      sx={{ flex: 1, minWidth: 240 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t("Add item")}
          placeholder={disabled ? t("Select the from branch first") : t("Search item by name, code or barcode")}
          error={Boolean(error)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

const WebStockTransferPage = () => {
  const { t } = useTranslation();

  const [sources, setSources] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [fromBranch, setFromBranch] = useState("");
  const [toBranch, setToBranch] = useState("");
  const [voucherDate, setVoucherDate] = useState(today);
  const [reasonCode, setReasonCode] = useState(REASON_CODES[0]);
  const [narration, setNarration] = useState("");
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", severity: "info" });
  const isPhone = useMediaQuery("(max-width:600px)");

  const tenancyId = localStorage.getItem("tenancyId");
  const token = localStorage.getItem("jwtToken");
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`/api/${tenancyId}/stock-transfers/web/options`, auth);
        if (cancelled) return;
        const src = res.data?.sources || [];
        setSources(src);
        setDestinations(res.data?.destinations || []);
        const loggedIn = localStorage.getItem("branchCode");
        if (src.length === 1) setFromBranch(src[0].branchCode);
        else if (loggedIn && src.some((b) => b.branchCode === loggedIn)) setFromBranch(loggedIn);
      } catch {
        if (!cancelled) setMessage({ text: t("Could not load branches. Reload the page to try again."), severity: "error" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenancyId, auth, t]);

  const toOptions = useMemo(() => destinations.filter((b) => b.branchCode !== fromBranch), [destinations, fromBranch]);

  // Transfer rate for the destination: MRP less any Stock Transfer Discount set for it, as on POS.
  const rateFor = useCallback(
    async (itemId, mrp, branch) => {
      if (!mrp) return mrp;
      try {
        const res = await axios.get(`/api/${tenancyId}/stock-transfer-discounts/item/${encodeURIComponent(itemId)}`, {
          ...auth,
          params: branch ? { branchId: branch } : {},
        });
        const d = res.data || {};
        if (d.discount_percent != null) return round2(mrp - (mrp * d.discount_percent) / 100);
        if (d.rate != null) return round2(d.rate);
      } catch {
        // No discount set up: transfer at MRP.
      }
      return mrp;
    },
    [tenancyId, auth]
  );

  const changeFrom = (code) => {
    if (code === fromBranch) return;
    setFromBranch(code);
    if (toBranch === code) setToBranch("");
    // Stock on hand was read for the old branch.
    setRows([]);
  };

  const changeTo = async (code) => {
    setToBranch(code);
    const repriced = await Promise.all(
      rows.map(async (r) => ({ ...r, rate: await rateFor(r.itemId, r.mrp, code) }))
    );
    setRows(repriced);
  };

  const pickItem = async (item) => {
    setMessage({ text: "", severity: "info" });
    if (rows.some((r) => r.itemId === item.itemId)) {
      setMessage({ text: `${item.itemName} ${t("is already in the list")}`, severity: "info" });
      return;
    }
    let batches = [];
    try {
      const res = await axios.get(`/api/${tenancyId}/inventory/item-batches`, {
        ...auth,
        params: { branchCode: fromBranch, itemId: item.itemId },
      });
      batches = (res.data || []).map((b) => ({ batch: b.batchNo ?? "", stock: Number(b.currentStock) || 0 }));
    } catch {
      setMessage({ text: t("Could not read the stock for this item. Try again."), severity: "error" });
      return;
    }
    if (batches.length === 0) {
      setMessage({ text: `${item.itemName}: ${t("no stock at")} ${fromBranch}`, severity: "warning" });
      return;
    }
    const mrp = Number(item.standardPrice) || 0;
    const rate = await rateFor(item.itemId, mrp, toBranch);
    setRows((prev) => [
      {
        key: crypto.randomUUID(),
        itemId: item.itemId,
        itemName: item.itemName,
        itemCode: item.itemCode,
        barcode: item.barcode,
        unit: item.unitName || "",
        taxRate: Number(item.taxRate) || 0,
        batches,
        batch: batches[0].batch,
        qty: "",
        mrp,
        rate,
      },
      ...prev,
    ]);
  };

  const updateRow = (key, patch) => setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const stockOf = (r) => r.batches.find((b) => b.batch === r.batch)?.stock ?? 0;

  const totalQty = rows.reduce((s, r) => s + (Number(r.qty) || 0), 0);
  const totalAmount = rows.reduce((s, r) => s + round2((Number(r.qty) || 0) * (Number(r.rate) || 0)), 0);

  const reset = () => {
    setRows([]);
    setNarration("");
    setReasonCode(REASON_CODES[0]);
  };

  const save = async () => {
    setMessage({ text: "", severity: "info" });
    if (!fromBranch || !toBranch) return setMessage({ text: t("Select the from and to branch"), severity: "warning" });
    if (rows.length === 0) return setMessage({ text: t("Add at least one item"), severity: "warning" });
    for (const r of rows) {
      const q = checkQuantity(r.qty);
      if (!q.ok || !(Number(r.qty) > 0)) {
        return setMessage({ text: `${r.itemName}: ${q.error || t("enter the quantity")}`, severity: "warning" });
      }
      if (Number(r.qty) > stockOf(r)) {
        return setMessage({
          text: `${r.itemName}: ${t("only")} ${fmtQty(stockOf(r))} ${t("in stock")}`,
          severity: "warning",
        });
      }
    }
    const to = destinations.find((b) => b.branchCode === toBranch) || {};
    const body = {
      id: crypto.randomUUID(),
      branch_code: fromBranch,
      to_branch_code: toBranch,
      to_branch_name: to.branchName || "",
      to_branch_state: to.branchState || "",
      to_branch_gst: to.branchGst || "",
      delivery_to_location: to.branchBuildingAddress || "",
      delivery_to_address1: to.branchAddress1 || "",
      delivery_to_address2: to.branchAddress2 || "",
      voucher_date: `${voucherDate}T${nowTime()}`,
      voucher_type: "STOCK_TRANSFER",
      reason_code: reasonCode,
      description: narration.trim() || "Stock Transfer",
      lines: rows.map((r) => ({
        id: crypto.randomUUID(),
        item_id: r.itemId,
        item_name: r.itemName,
        item_code: r.itemCode,
        barcode: r.barcode,
        unit: r.unit,
        batch: r.batch,
        qty: Number(r.qty),
        rate: Number(r.rate) || 0,
        standard_price: r.mrp,
        amount: round2(Number(r.qty) * (Number(r.rate) || 0)),
        tax_rate: r.taxRate,
        cess_rate: 0,
      })),
    };
    setSaving(true);
    try {
      const res = await axios.post(`/api/${tenancyId}/stock-transfers/web`, body, auth);
      const d = res.data || {};
      setMessage({
        text: `${t("Stock transfer saved")}${d.voucherNumber ? ` - ${d.voucherNumber}` : ""}: ${fromBranch} → ${toBranch}, ${rows.length} ${t("items")}. ${t("The receiving branch accepts it in Accept Stock.")}`,
        severity: "success",
      });
      reset();
    } catch (err) {
      setMessage({ text: err?.response?.data?.error || t("Could not save. Try again."), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const headCell = { color: "#fff", fontWeight: 700, whiteSpace: "nowrap" };
  const emptyText = fromBranch ? t("Search an item above to add it") : t("Select the from branch first");
  const batchCell = (r) =>
    r.batches.length > 1 ? (
      <Select size="small" variant="standard" value={r.batch} onChange={(e) => updateRow(r.key, { batch: e.target.value })}>
        {r.batches.map((b) => (
          <MenuItem key={b.batch} value={b.batch}>
            {b.batch || t("(no batch)")}
          </MenuItem>
        ))}
      </Select>
    ) : (
      r.batch || "—"
    );
  const qtyField = (r, props) => (
    <TextField
      size="small"
      variant="standard"
      value={r.qty}
      error={Number(r.qty) > stockOf(r)}
      onChange={(e) => updateRow(r.key, { qty: e.target.value })}
      inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
      {...props}
    />
  );
  const rateField = (r, props) => (
    <TextField
      size="small"
      variant="standard"
      value={r.rate}
      onChange={(e) => updateRow(r.key, { rate: e.target.value })}
      inputProps={{ inputMode: "decimal", style: { textAlign: "right" } }}
      {...props}
    />
  );
  const removeButton = (r) => (
    <IconButton size="small" aria-label={t("Remove")} onClick={() => setRows((p) => p.filter((x) => x.key !== r.key))}>
      <RemoveIcon fontSize="small" />
    </IconButton>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: "100vw", boxSizing: "border-box" }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        {t("Stock Transfer")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("Send stock from one of your branches to another branch. The receiving branch accepts it in Accept Stock, as with a POS transfer.")}{" "}
        <Link component={RouterLink} to="/stocktransfer-out-report">
          {t("Stock Transfer Out Report")}
        </Link>
      </Typography>

      {message.text && (
        <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage({ text: "", severity: "info" })}>
          {message.text}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="wst-from">{t("From branch")}</InputLabel>
            <Select labelId="wst-from" value={fromBranch} label={t("From branch")} onChange={(e) => changeFrom(e.target.value)}>
              {sources.map((b) => (
                <MenuItem key={b.branchCode} value={b.branchCode}>
                  {branchLabel(b)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="wst-to">{t("To branch")}</InputLabel>
            <Select labelId="wst-to" value={toBranch} label={t("To branch")} onChange={(e) => changeTo(e.target.value)}>
              {toOptions.map((b) => (
                <MenuItem key={b.branchCode} value={b.branchCode}>
                  {branchLabel(b)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label={t("Date")}
            value={voucherDate}
            onChange={(e) => setVoucherDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today() }}
            sx={{ minWidth: 170 }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="wst-reason">{t("Reason")}</InputLabel>
            <Select labelId="wst-reason" value={reasonCode} label={t("Reason")} onChange={(e) => setReasonCode(e.target.value)}>
              {REASON_CODES.map((r) => (
                <MenuItem key={r} value={r}>
                  {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
        <TextField
          size="small"
          fullWidth
          label={t("Narration")}
          value={narration}
          onChange={(e) => setNarration(e.target.value)}
          inputProps={{ maxLength: 250 }}
          sx={{ mb: 2 }}
        />
        <ItemPicker tenancyId={tenancyId} auth={auth} disabled={!fromBranch} onPick={pickItem} />
      </Paper>

      {isPhone ? (
        <Stack spacing={1} sx={{ mb: 2 }}>
          {rows.length === 0 ? (
            <Paper sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>{emptyText}</Paper>
          ) : (
            rows.map((r) => (
              <Paper key={r.key} sx={{ p: 1.5 }}>
                <Stack direction="row" alignItems="flex-start">
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography fontWeight={600}>{r.itemName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("Batch")}: {batchCell(r)} · {t("In stock")}: {fmtQty(stockOf(r))} {r.unit} · {t("MRP")} {money(r.mrp)}
                    </Typography>
                  </Box>
                  {removeButton(r)}
                </Stack>
                <Stack direction="row" spacing={2} alignItems="flex-end" sx={{ mt: 1 }}>
                  {qtyField(r, { label: t("Qty"), sx: { width: 90 } })}
                  {rateField(r, { label: t("Rate"), sx: { width: 100 } })}
                  <Typography sx={{ flexGrow: 1, textAlign: "right" }} fontWeight={600}>
                    {money((Number(r.qty) || 0) * (Number(r.rate) || 0))}
                  </Typography>
                </Stack>
              </Paper>
            ))
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#1976d2" }}>
                <TableCell sx={headCell}>{t("Item")}</TableCell>
                <TableCell sx={headCell}>{t("Batch")}</TableCell>
                <TableCell sx={headCell} align="right">{t("In stock")}</TableCell>
                <TableCell sx={headCell} align="right">{t("Qty")}</TableCell>
                <TableCell sx={headCell} align="right">{t("MRP")}</TableCell>
                <TableCell sx={headCell} align="right">{t("Rate")}</TableCell>
                <TableCell sx={headCell} align="right">{t("Amount")}</TableCell>
                <TableCell sx={headCell} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: "text.secondary" }}>
                    {emptyText}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.key}>
                    <TableCell sx={{ minWidth: 160 }}>
                      {r.itemName}
                      {r.unit ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {r.unit}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{batchCell(r)}</TableCell>
                    <TableCell align="right">{fmtQty(stockOf(r))}</TableCell>
                    <TableCell align="right">{qtyField(r, { sx: { width: 80 } })}</TableCell>
                    <TableCell align="right">{money(r.mrp)}</TableCell>
                    <TableCell align="right">{rateField(r, { sx: { width: 90 } })}</TableCell>
                    <TableCell align="right">{money((Number(r.qty) || 0) * (Number(r.rate) || 0))}</TableCell>
                    <TableCell padding="none">{removeButton(r)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <Typography sx={{ flexGrow: 1 }}>
          {rows.length} {t("items")} · {t("Qty")} {fmtQty(totalQty)} · {t("Amount")} <b>{money(totalAmount)}</b>
        </Typography>
        <Button onClick={reset} disabled={saving}>
          {t("Clear")}
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || !fromBranch || !toBranch || rows.length === 0}>
          {saving ? <CircularProgress size={22} /> : t("Save Transfer")}
        </Button>
      </Stack>
    </Box>
  );
};

export default WebStockTransferPage;
